import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ArrHlp } from 'src/lib/fliko/helpers/array.helper';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { AppInviteRequest } from 'src/lib/fliko/models/http/appInviteRequest.data';
import { MsgStatus } from 'src/lib/fliko/models/integrations/sendDataBase.data';
import { SMSType } from 'src/lib/fliko/models/integrations/sendSMS.data';
import { SMSQueueData } from 'src/lib/fliko/models/messageQueue/sms.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { DataTableConfig } from 'src/app/shared/components/clickable-row-table/models/data-table-config.models';
import { ValuePhoneNumberTable, ValueStringTable } from 'src/app/shared/components/clickable-row-table/models/value-table.model';
import { INVITE_TIME_DIFF } from 'src/app/shared/components/details-contacts/details-contacts.component';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { PushIdService } from 'src/app/shared/services/pushId.service';
import { SendSMSService } from 'src/app/shared/services/send-sms.service';
import { SnackBarService } from 'src/app/shared/services/snackBar.service';
import { TemplateService } from 'src/app/shared/services/template-service/template.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { AppInviteService } from '../../services/appInvite.service';
import { BulkInvitesTableColumns, BulkInvitesTableRows } from '../../services/models/table/app-requests-table-model';
import { BasicResidentElement } from '../../services/models/users-table.data';
import { UsersCommDataService } from '../../services/users-comm-data.service';
import { UsersPRSDataService } from '../../services/users-prs-data.service';

@Component({
  selector: 'app-bulk-app-invitations',
  templateUrl: './bulk-app-invitations.component.html',
  styleUrls: ['./bulk-app-invitations.component.scss']
})
export class BulkAppInvitationsComponent implements OnInit, OnDestroy {
  public readonly locHelper = new LocalizationHelper(this.ls);

  public tableConfig = new DataTableConfig({
    dataColumns: BulkInvitesTableColumns.columns,
    paginator: true,
    searchKeyNames: ['fullName', 'phone']
  });

  public tableData: BulkInvitesTableRows[] = [];
  public tablePage$ = new BehaviorSubject<number>(0);
  public loaded = false;
  public isSubmitting = false;
  public tableSearch = '';
  public resToInvite: ResidentData[] = [];

  private residentsMap = new Map<string, ResidentData>();
  private subscription$ = new Subscription();

  constructor(
    public templateService: TemplateService,
    private commDataService: UsersCommDataService,
    private prsDataService: UsersPRSDataService,
    private ls: LocalizationService,
    private smsService: SendSMSService,
    private dialogService: DialogService,
    private inviteService: AppInviteService,
    private errorHandlingService: ErrorHandlingService,
    private snackBarService: SnackBarService,
    private pushId: PushIdService,
    private ctxDS: ContextDataService
  ) {}

  ngOnInit(): void {
    const dataObservable$: Observable<BasicResidentElement[] | null> =
      this.ctxDS.peekCurrentEstate()?.type == EstateType.COMMUNITY
        ? this.commDataService.getLandlordsTabData$()
        : this.prsDataService.getResidentsTabData$();
    this.subscription$.add(
      combineLatest([dataObservable$, this.inviteService.getInvites$()]).subscribe(([resElemArr, invites]) => {
        if (resElemArr) {
          const invitedResidents = new Set<string>();
          invites.forEach((invitation) => {
            if (Date.now() - TimeHlp.timestamp(invitation.createDate) < INVITE_TIME_DIFF) invitedResidents.add(invitation.id);
          });
          this.residentsMap = new Map(resElemArr.map((x) => [x.id, x.resident]));
          this.tableData = [];
          resElemArr.forEach((element) => {
            if (!element.resident.userId) {
              const phoneNumber = ArrHlp.get(element.resident.phone, 0);
              this.tableData.push({
                id: new ValueStringTable(element.id),
                fullName: new ValueStringTable(ToString.fullName(element.resident)),
                phone: new ValuePhoneNumberTable(phoneNumber, this.ls.translate('label.empty').toLocaleUpperCase()),
                checked: false,
                invited: invitedResidents.has(element.resident.id)
              });
            }
          });
          this.loaded = true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public onResidentSelected(id: string, value: boolean): void {
    const res = this.residentsMap.get(id)!;
    if (value && !this.resToInvite.find((r) => r.id == id)) this.resToInvite.push(res);
    else this.resToInvite = this.resToInvite.filter((r) => r.id != id);
  }

  public checkAll(value: boolean): void {
    this.tableData.forEach((row) => {
      if (row.phone.isValid && !row.invited) {
        row.checked = value;
        this.onResidentSelected(row.id.value, value);
      }
    });
  }

  public async onSubmit(): Promise<void> {
    this.dialogService
      .openConfirmationDialog({
        text: this.ls.trReplace('guard.sendBulkInvitations', { $amount: this.resToInvite.length }),
        acceptKey: 'button.send',
        cancelKey: 'button.cancel'
      })
      .subscribe(async (response) => {
        if (response) {
          const estate = this.ctxDS.peekCurrentEstate();
          if (!estate) throw new Error('Invalid estate');
          const estateId = estate.id;
          const queueData: SMSQueueData = {
            id: this.pushId.create(),
            creatorId: this.ctxDS.peekCurrentUserId(),
            createDate: Date.now(),
            sendDate: Date.now(),
            estateId,
            receiver: null,
            receivers: this.resToInvite.map((r) => r.id),
            status: MsgStatus.PENDING,
            type: estate.acApp ? SMSType.APP_INVITE_ACA : SMSType.APP_INVITE,
            refId: null
          };
          const receivers = this.resToInvite.map((res) => {
            return { id: res.id, phoneNumber: ArrHlp.get(res.phone, 0)! };
          });
          const requestData: AppInviteRequest = {
            estateId,
            queueRefId: queueData.id,
            receivers
          };
          try {
            this.isSubmitting = true;
            await this.smsService.sendSMS(queueData);
            await this.inviteService.requestAppInviteUpdate(requestData);
            this.snackBarService.showSuccess('success.sendBulkInvitations');
            this.dialogService.closeAllDialogs();
            this.isSubmitting = false;
          } catch (e) {
            this.errorHandlingService.showGlobalError('error.sendBulkInvitations', e);
            this.isSubmitting = false;
          }
        }
      });
  }
}
